import { Observable, of } from 'rxjs';
import { Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { Location } from '@angular/common';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { AuthenticationService } from 'src/app/services/authentication/authentication.service';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { AlertService } from 'src/app/services/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'src/app/services/toast/toast.service';
import { ModalController, Platform } from '@ionic/angular';
import { environment } from 'src/environments/environment';
import { EulaService } from 'src/app/services/eula/eula.service';
import { EULA } from 'src/app/models/eula.model';
import { EULAModalPage } from 'src/app/modals/eula-modal/eula-modal.page';

@Injectable({
  providedIn: 'root'
})
export class ErrorInterceptorService implements HttpInterceptor {
  presentingEULA = new Map<string, number>();

  constructor(
    private router: Router,
    private location: Location,
    private loading: LoadingService,
    private alertService: AlertService,
    public translate: TranslateService,
    private authentication: AuthenticationService,
    private platform: Platform,
    private eulaService: EulaService,
    private modalController: ModalController,
    private toastService: ToastService,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(catchError(err => {
      if(err.status === 401) { // Unauthorized.
        if (err.error?.response_code == "RINKNET_LOGIN_INVALID") {
          this.toastService.presentToast(this.translate.instant('LOGIN.VerifyUser.rinknetLoginFailed'), 5000, "", "top");
          return of(new HttpResponse({
            body: err.error
          }));
        } else {
          this.authentication.logout().then(() => {
            this.authentication.isAuthenticated.next(false);

            // Only update the attemptedRoute if the page was not a login page or the companion-accounts page
            if(this.router.url.includes('/home') && this.router.url != '/home/companion-accounts') { 
              this.authentication.attemptedRoute.next(this.router.url);
            }

            this.router.navigateByUrl('/login');
          });
        }
      } else if(err.status == 412) { // Precondition's not met.
        if(err.error.response_code == "APP_VERSION_OUT_OF_DATE") {
          this.authentication.logout();
          this.router.navigateByUrl('/login');

          let closeButtonText = this.translate.instant('Close');
          if (this.platform.is('ios') || this.platform.is('android')) {
            closeButtonText = this.translate.instant('Update');
          }

          this.alertService.infoAlert(err.error.message, closeButtonText).then(dismissal => {
            if (dismissal.role == "yes") {
              let url = "";

              // If on iOS or Android, go to the store page to update the app 
              if (this.platform.is("ios")) {
                url = environment.APP_STORE_URL;
                window.open(url, "_top");
              } else if (this.platform.is('android')) {
                url = environment.PLAY_STORE_URL;
                window.open(url, "_blank");
              }
            }
          });
        } else if (err.error.response_code == "EULA_NEEDS_APPROVAL") {
          this.eulaService.getCurrentEULA().subscribe(async (eula: EULA) => {
            const modal = await this.modalController.create({ // Display EULA
              component: EULAModalPage,
              canDismiss: true,
              componentProps: {
                eula: eula
              },
              cssClass: 'one-rink-modal'
            });

            let session: string = request.headers.get("Authorization");
            if(this.presentingEULA?.size <= 0 || !this.presentingEULA.has(session) || this.presentingEULA.get(session).valueOf() != eula.id) {
              this.presentingEULA.set(session, eula.id);
              await modal.present();
            }
          });
        } else if (err.error.response_code == "USER_SETUP_INCOMPLETE") {
          this.router.navigateByUrl('/user-setup');
        } 
        else {
          this.alertService.errorAlert(`${err.error.message}. ${this.translate.instant("ERROR.continuedError")}`);
        }
      } else if (err.status == 500) { // Server Error.
        this.alertService.errorAlert(`${this.translate.instant("ERROR.errorOccured")}`);
      } else if (err.status == 404) { // Not found.
        this.alertService.warningAlert(this.translate.instant("ERROR.notFoundExplanation")).then(() => {
          this.location.back();
        });
      } else if (err.status == 403) { // Forbidden. (Only acted upon in special circumstances)
        if(err && err.error && err.error.response_code) {
          if (err.error.response_code == "APP_USER_NOT_ACTIVE") {
            this.alertService.errorAlert(err.error.message);
          }
        }
      } else if(err.status == 426) { // User has tried to upload a file that exceeds thier storage limit. Warn them why it doesnt work and how to upgrade
        this.alertService.warningAlert(this.translate.instant("ERROR.beyondStorageLimit"));
      }

      this.loading.dismiss(); // Closes the loader if there has been an error in an API call

      return of(err.error);
    }));
  }
}
